import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';
import { DialogOpenEvent } from '@theme/dialog';

/**
 * Free shipping over amount feature.
 *
 * - `<free-shipping-message>`: live "spend X more" message on product pages.
 * - `<free-shipping-addons>`: compact add-on recommendations carousel (cart drawer + product page).
 *
 * The cart drawer progress bar is fully server-rendered (snippets/free-shipping-progress.liquid)
 * and updates via the theme's existing Section Rendering morph — no JS needed there.
 *
 * All components subscribe to the theme's single `cart:update` event and share one
 * cart state read per mutation via `readCart()`.
 */

// `Theme` is declared with top-level `const` in snippets/scripts.liquid, so it is a
// global lexical binding — NOT a property of `window`. Reference it bare, like the
// rest of the theme's modules do.
const themeGlobal = /** @type {any} */ (typeof Theme !== 'undefined' ? Theme : {});
const config = themeGlobal.freeShippingOverAmount ?? { enabled: false, thresholdCents: 0 };
const translations = themeGlobal.translations ?? {};

/** @type {Promise<any> | null} Shared cart state; refreshed once per cart mutation. */
let cartPromise = null;

/** @type {string | null} Product id of the most recently added/changed cart item. */
let lastChangedProductId = null;

/** Max cart items fanned out as recommendation anchors per update. */
const MAX_CART_ANCHORS = 4;

/** @type {Map<string, any[]>} Session cache: `${productId}:${intent}:${limit}` → products. */
const recommendationsCache = new Map();

function readCart({ force = false } = {}) {
  if (!cartPromise || force) {
    cartPromise = fetch(`${themeGlobal.routes?.cart_url ?? '/cart'}.js`).then((response) => response.json());
  }
  return cartPromise;
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = event.detail ?? {};

  if (detail.data?.productId) {
    lastChangedProductId = String(detail.data.productId);
  }

  const resource = detail.resource;
  // /cart/change.js responses carry the full cart; /cart/add.js responses do not.
  if (resource && typeof resource.total_price === 'number' && Array.isArray(resource.items)) {
    cartPromise = Promise.resolve(resource);
  } else {
    readCart({ force: true });
  }
});

/**
 * Formats a value in cents using the shop's money format (keeps EGP formatting consistent).
 * @param {number} cents
 * @param {{ wholeUnits?: boolean }} [options] - wholeUnits: no decimals, rounded up.
 * @returns {string}
 */
function formatMoney(cents, { wholeUnits = false } = {}) {
  const format = themeGlobal.moneyFormat || '{{ amount }}';
  const value = cents / 100;
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const key = format.match(placeholder)?.[1] ?? 'amount';

  if (wholeUnits) {
    return format.replace(placeholder, Math.ceil(value).toLocaleString('en-US'));
  }

  let formatted;
  switch (key) {
    case 'amount_no_decimals':
      formatted = Math.round(value).toLocaleString('en-US');
      break;
    case 'amount_with_comma_separator':
      formatted = value
        .toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = Math.round(value)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      break;
    default:
      formatted = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return format.replace(placeholder, formatted);
}

/**
 * @param {any} cart
 * @returns {number} Remaining amount in cents until free shipping, floored at 0.
 */
function remainingCents(cart) {
  return Math.max(config.thresholdCents - (cart?.total_price ?? 0), 0);
}

/**
 * Live shipping message under the add-to-cart button on product pages.
 * Server-rendered initial state; updates on every cart mutation.
 */
class FreeShippingMessage extends HTMLElement {
  #listeners;

  connectedCallback() {
    if (!config.enabled || config.thresholdCents <= 0) return;
    this.#listeners = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#onCartUpdate.cancel();
  }

  #onCartUpdate = debounce(async () => {
    const cart = await readCart();
    if (!this.isConnected) return;

    const textElement = this.querySelector('[data-free-shipping-text]');
    if (!textElement) return;

    const remaining = remainingCents(cart);
    this.classList.toggle('free-shipping-message--unlocked', remaining === 0);
    textElement.innerHTML =
      remaining === 0
        ? translations.free_shipping_unlocked
        : (translations.free_shipping_remaining_html ?? '').replace(
            '[[amount]]',
            formatMoney(remaining, { wholeUnits: true })
          );
  }, 150);
}

if (!customElements.get('free-shipping-message')) {
  customElements.define('free-shipping-message', FreeShippingMessage);
}

/**
 * Compact add-on recommendations carousel.
 *
 * data-context="cart"    → driven by all cart items (fan-out, capped at MAX_CART_ANCHORS,
 *                          last added first; results merged and ranked by how many cart
 *                          items recommend the same product).
 * data-context="product" → driven by data-product-id (the current product).
 *
 * Excludes products already in the cart and the source product; when the cart is
 * below the threshold, results are sorted so prices that best close the remaining
 * gap come first (cheapest item that crosses the threshold, then closest below it).
 */
class FreeShippingAddons extends HTMLElement {
  #listeners;
  /** @type {AbortController | undefined} */
  #fetchController;

  connectedCallback() {
    if (!config.enabled) {
      this.hidden = true;
      return;
    }
    this.#listeners = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleUpdate, { signal: this.#listeners.signal });
    this.addEventListener('click', this.#onClick, { signal: this.#listeners.signal });

    // A cart carried over from a previous session (or a refresh with existing cart
    // items) reaches this point with `cartPromise` unset, so the update below reads
    // the cart correctly. But if the drawer's initial connect races with the dialog
    // upgrading/moving in the DOM (see cart-drawer-component/DialogComponent), the
    // scheduled update can be cancelled by an intervening disconnect and never retried
    // until the next cart mutation — leaving the carousel hidden the first time the
    // drawer is opened. Re-validating with a forced cart re-read on every drawer open
    // closes that gap regardless of the exact race, and is correct anyway since the
    // cart can also go stale between page load and open (edited in another tab).
    const cartDrawer = this.dataset.context === 'cart' ? this.closest('cart-drawer-component') : null;
    cartDrawer?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal: this.#listeners.signal });

    // Debounced rather than immediate: when this element is inserted by the drawer
    // morph mid-`cart:update` dispatch, an immediate update would read the stale
    // pre-mutation cart (and hide) while missing the event that just fired.
    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#fetchController?.abort();
    this.#scheduleUpdate.cancel();
  }

  #handleDialogOpen = () => {
    readCart({ force: true });
    this.#scheduleUpdate();
  };

  // Debounce absorbs rapid successive cart mutations and lets the drawer's
  // section morph settle before we re-render inside it.
  #scheduleUpdate = debounce(() => this.#update(), 200);

  get #track() {
    return this.querySelector('[data-addons-track]');
  }

  #hide() {
    this.hidden = true;
    const track = this.#track;
    if (track) track.innerHTML = '';
  }

  async #update() {
    const cart = await readCart();
    if (!this.isConnected) return;

    // Anchors: in cart context, every cart item (capped, last-added first) — the API
    // only accepts one product_id per request, so cart-wide coverage is a fan-out.
    /** @type {string[]} */
    let anchorIds = [];
    if (this.dataset.context === 'cart') {
      if (!cart?.items?.length) return this.#hide();
      const ids = [...new Set(cart.items.map((item) => String(item.product_id)))];
      if (lastChangedProductId && ids.includes(lastChangedProductId)) {
        ids.splice(ids.indexOf(lastChangedProductId), 1);
        ids.unshift(lastChangedProductId);
      }
      anchorIds = ids.slice(0, MAX_CART_ANCHORS);
    } else if (this.dataset.productId) {
      anchorIds = [this.dataset.productId];
    }
    if (!anchorIds.length) return this.#hide();

    // Cancel any stale in-flight batch so a fast follow-up mutation wins.
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const { signal } = this.#fetchController;
    this.#track?.setAttribute('aria-busy', 'true');

    let candidates;
    try {
      const fetchLimit = Number(this.dataset.fetchLimit) || 10;
      const intent = this.dataset.intent || 'related';
      const results = await Promise.allSettled(
        anchorIds.map((anchorId) => this.#fetchProducts(anchorId, intent, fetchLimit))
      );
      if (signal.aborted) return;
      candidates = this.#mergeResults(results);
      if (!candidates.length && results.every((result) => result.status === 'rejected')) return this.#hide();
    } finally {
      this.#track?.setAttribute('aria-busy', 'false');
    }

    const inCartIds = new Set((cart?.items ?? []).map((item) => String(item.product_id)));
    const anchorIdSet = new Set(anchorIds);
    candidates = candidates.filter(
      (product) => !inCartIds.has(String(product.id)) && !anchorIdSet.has(String(product.id))
    );

    const remaining = remainingCents(cart);
    if (remaining > 0) {
      const crossers = candidates.filter((p) => p.price >= remaining).sort((a, b) => a.price - b.price);
      const below = candidates.filter((p) => p.price < remaining).sort((a, b) => b.price - a.price);
      candidates = [...crossers, ...below];
    }

    candidates = candidates.slice(0, Number(this.dataset.limit) || 4);
    if (!candidates.length) return this.#hide();

    this.#render(candidates);
  }

  /**
   * Fetches recommendations for one anchor, with a session cache so repeat cart
   * mutations only hit the network for anchors not seen before.
   * @param {string} productId
   * @param {string} intent
   * @param {number} limit
   * @returns {Promise<any[]>}
   */
  async #fetchProducts(productId, intent, limit) {
    const cacheKey = `${productId}:${intent}:${limit}`;
    const cached = recommendationsCache.get(cacheKey);
    if (cached) return cached;

    const url = `${this.dataset.url}?product_id=${productId}&limit=${limit}&intent=${intent}`;
    const response = await fetch(url, { signal: this.#fetchController?.signal });
    if (!response.ok) return [];
    const { products } = await response.json();
    recommendationsCache.set(cacheKey, products ?? []);
    return products ?? [];
  }

  /**
   * Merges per-anchor results, deduped by product id. Products recommended by
   * several cart items rank first; ties keep anchor order (last added first).
   * @param {PromiseSettledResult<any[]>[]} results
   * @returns {any[]}
   */
  #mergeResults(results) {
    /** @type {Map<number, { product: any, hits: number, order: number }>} */
    const byId = new Map();
    let order = 0;
    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      for (const product of result.value) {
        const entry = byId.get(product.id);
        if (entry) {
          entry.hits += 1;
        } else {
          byId.set(product.id, { product, hits: 1, order: order++ });
        }
      }
    }
    return [...byId.values()].sort((a, b) => b.hits - a.hits || a.order - b.order).map((entry) => entry.product);
  }

  /** @param {any[]} products */
  #render(products) {
    const track = this.#track;
    if (!track) return;

    track.innerHTML = '';
    for (const product of products) {
      track.appendChild(this.#buildCard(product));
    }

    this.hidden = false;
  }

  /** @param {any} product */
  #buildCard(product) {
    const variant = (product.variants ?? []).find((v) => v.available);

    const card = document.createElement('div');
    card.className = 'free-shipping-addons__card';

    const media = document.createElement('a');
    media.className = 'free-shipping-addons__media';
    media.href = product.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (product.featured_image) {
      const image = document.createElement('img');
      const separator = product.featured_image.includes('?') ? '&' : '?';
      image.src = `${product.featured_image}${separator}width=180`;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 90;
      image.height = 90;
      media.appendChild(image);
    }
    card.appendChild(media);

    const info = document.createElement('div');
    info.className = 'free-shipping-addons__info';

    const title = document.createElement('a');
    title.className = 'free-shipping-addons__title';
    title.href = product.url;
    title.textContent = product.title;
    info.appendChild(title);

    const price = document.createElement('span');
    price.className = 'free-shipping-addons__price';
    price.innerHTML = formatMoney(product.price);
    info.appendChild(price);
    card.appendChild(info);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button free-shipping-addons__add';
    if (variant) {
      button.textContent = translations.free_shipping_addons_add ?? 'Add';
      button.dataset.variantId = String(variant.id);
      button.dataset.productId = String(product.id);
      button.setAttribute('aria-label', `${translations.free_shipping_addons_add ?? 'Add'} — ${product.title}`);
    } else {
      button.textContent = translations.sold_out ?? 'Sold out';
      button.disabled = true;
    }
    card.appendChild(button);

    return card;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-variant-id]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    this.#addToCart(button);
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    button.disabled = true;
    button.classList.add('free-shipping-addons__add--loading');

    const formData = new FormData();
    formData.append('id', /** @type {string} */ (button.dataset.variantId));
    formData.append('quantity', '1');

    // Request server-rendered sections so the drawer (items, progress bar, totals)
    // comes back consistent in the same round trip.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    try {
      const response = await fetch(themeGlobal.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      const data = await response.json();
      if (data.status) throw new Error(data.message || 'Failed to add to cart');

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'free-shipping-addons', {
          source: 'free-shipping-addons',
          itemCount: 1,
          productId: String(button.dataset.productId ?? data.product_id ?? ''),
          variantId: button.dataset.variantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      button.disabled = false;
    } finally {
      button.classList.remove('free-shipping-addons__add--loading');
    }
  }
}

if (!customElements.get('free-shipping-addons')) {
  customElements.define('free-shipping-addons', FreeShippingAddons);
}
